import React, { useMemo, useRef, useState } from "react";

import { useInfoEndpoints } from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { Pill } from "src/base-components/Pill";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { PALETTE } from "src/design-system/utils/colorPalette";
import { useOnScreen } from "src/hooks/useOnScreen";

export const ActualVersionsOverview: React.FC<{
  workspaces: WorkspaceDataplane[];
  selectedVersion: string | null;
  onSelectVersion: (version: string | null) => void;
  onFilteredWorkspacesChange?: (workspaceIds: string[] | null) => void;
  allVersions: string[];
}> = ({
  workspaces,
  selectedVersion,
  onSelectVersion,
  onFilteredWorkspacesChange,
  allVersions,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState<Set<string>>(
    new Set(),
  );
  const parentRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(parentRef);

  // When user explicitly clicks to load, ignore visibility check
  const workspaceQueries = useInfoEndpoints(workspaces, isLoaded || isVisible);

  // Create stable serialized key for query state
  const queryStateKey = useMemo(
    () =>
      workspaceQueries
        .map(
          (q) =>
            `${q.isLoading}-${q.error ? "err" : "ok"}-${q.data?.flow_services_version || ""}`,
        )
        .sort()
        .join("|"),
    [workspaceQueries],
  );

  // Map query results back to workspace format
  const workspaceInfoResults = useMemo(
    () =>
      workspaces.map((workspace, index) => ({
        workspace,
        data: workspaceQueries[index].data,
        error: workspaceQueries[index].error,
        isLoading: workspaceQueries[index].isLoading,
      })),
    [workspaces, queryStateKey],
  );

  // Compute version counts and status after loading
  const {
    versionCounts,
    versionFailureCounts,
    totalWorkspaces,
    hasErrors,
    isLoading: anyLoading,
    workspacesByVersion,
  } = useMemo(() => {
    if (!isLoaded) {
      return {
        versionCounts: {} as Record<string, number>,
        versionFailureCounts: {} as Record<string, number>,
        totalWorkspaces: 0,
        hasErrors: false,
        isLoading: false,
        workspacesByVersion: {} as Record<string, string[]>,
      };
    }

    const versionCounts: Record<string, number> = {};
    const versionFailureCounts: Record<string, number> = {};
    const workspacesByVersion: Record<string, string[]> = {};
    let hasErrors = false;
    let isLoading = false;

    workspaceInfoResults.forEach(
      ({ workspace, data, error, isLoading: wsLoading }) => {
        if (wsLoading) {
          isLoading = true;
          return;
        }

        if (error) {
          hasErrors = true;
          return;
        }

        if (data?.flow_services_version) {
          const version = data.flow_services_version;
          versionCounts[version] = (versionCounts[version] || 0) + 1;

          if (!workspacesByVersion[version]) {
            workspacesByVersion[version] = [];
          }
          workspacesByVersion[version].push(workspace.id);

          if (workspace.status === "FAILURE") {
            versionFailureCounts[version] =
              (versionFailureCounts[version] || 0) + 1;
          }
        }
      },
    );

    const totalWorkspaces = Object.values(versionCounts).reduce(
      (a, b) => a + b,
      0,
    );

    return {
      versionCounts,
      versionFailureCounts,
      totalWorkspaces,
      hasErrors,
      isLoading,
      workspacesByVersion,
    };
  }, [
    isLoaded,
    workspaces.length,
    workspaceInfoResults.map((r) => r.workspace.id).join(","),
    workspaceInfoResults
      .map((r) => r.data?.flow_services_version || "none")
      .join(","),
    workspaceInfoResults.map((r) => (r.error ? "error" : "ok")).join(","),
    workspaceInfoResults
      .map((r) => (r.isLoading ? "loading" : "done"))
      .join(","),
  ]);

  // Notify parent about filtered workspaces when selection changes
  React.useEffect(() => {
    if (onFilteredWorkspacesChange) {
      if (selectedVersion && workspacesByVersion[selectedVersion]) {
        onFilteredWorkspacesChange(workspacesByVersion[selectedVersion]);
      } else {
        onFilteredWorkspacesChange(null);
      }
    }
  }, [selectedVersion]);

  const handleLoadVersions = () => {
    setIsLoaded(true);
    setLoadingWorkspaces(new Set(workspaces.map((ws) => ws.id)));
  };

  // Track when workspaces finish loading
  React.useEffect(() => {
    if (!isLoaded) return;

    const stillLoading = new Set<string>();
    workspaceInfoResults.forEach(({ workspace, isLoading: wsLoading }) => {
      if (wsLoading) {
        stillLoading.add(workspace.id);
      }
    });
    setLoadingWorkspaces(stillLoading);
  }, [isLoaded, workspaceInfoResults]);

  // Reuse the canonical order from allVersions for any version that is also in
  // the desired set, then append actual-only versions (versions that exist in
  // actual but not in desired) using the same numeric-aware comparator. This
  // keeps bar segments and legend pills in lockstep with the Desired bar.
  const knownVersions = allVersions.filter((v) => versionCounts[v] > 0);
  const actualOnlyVersions = Object.keys(versionCounts)
    .filter((v) => !allVersions.includes(v))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const versions = [...knownVersions, ...actualOnlyVersions];
  const colors = PALETTE;

  // Map version to color index (based on allVersions for consistency with Desired)
  const versionColorIndex: Record<string, number> = {};
  allVersions.forEach((v, i) => {
    versionColorIndex[v] = i;
  });

  // Assign colors to any versions not in allVersions (e.g., actual versions that differ from desired)
  // Start offset after allVersions to avoid color conflicts
  actualOnlyVersions.forEach((v, i) => {
    versionColorIndex[v] = allVersions.length + i;
  });

  if (!isLoaded) {
    return (
      <div className="mb-4 w-full">
        <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div>
            <h3 className="font-inter-semibold-14px text-gray-900">
              Actual Versions Overview
            </h3>
            <p className="mt-1 font-inter-normal-12px text-gray-600">
              Click to load actual version information from all{" "}
              {workspaces.length} workspaces
            </p>
          </div>
          <Button size="sm" variant="secondary" onClick={handleLoadVersions}>
            Load Actual Versions
          </Button>
        </div>
      </div>
    );
  }

  if (anyLoading || loadingWorkspaces.size > 0) {
    const loadedCount = workspaces.length - loadingWorkspaces.size;
    return (
      <div className="mb-4 w-full rounded-lg border border-gray-200 bg-blue-50 p-4">
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-inter-semibold-14px text-blue-900">
                Loading Actual Versions...
              </h3>
              <p className="mt-1 font-inter-normal-12px text-blue-700">
                Loaded {loadedCount} of {workspaces.length} workspaces
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-4 w-4 animate-spin rounded-full border-blue-600 border-b-2"></div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 w-full rounded-full bg-blue-200">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${(loadedCount / workspaces.length) * 100}%` }}
            ></div>
          </div>

          {/* Show partial results if we have some data */}
          {Object.keys(versionCounts).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {versions.map((version) => (
                <Pill key={version} size="sm" variant="outlined-gray">
                  <span className="font-code-12">{version}</span>
                  <span className="ml-1 font-inter-normal-12px text-gray-500">
                    ({versionCounts[version] || 0})
                  </span>
                </Pill>
              ))}
            </div>
          )}
        </div>

        {/* Hidden div to trigger lazy loading */}
        <div ref={parentRef} className="hidden" />
      </div>
    );
  }

  if (totalWorkspaces === 0) {
    return (
      <div className="mb-4 w-full">
        <div className="rounded-lg border border-gray-200 bg-yellow-50 p-4">
          <h3 className="font-inter-semibold-14px text-yellow-900">
            Actual Versions Overview
          </h3>
          <p className="mt-1 font-inter-normal-12px text-yellow-700">
            No actual version data available
            {hasErrors && " (some workspaces failed to load)"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 w-full rounded-lg border border-gray-200 p-4">
      <div className="">
        <h3 className="mb-2 font-inter-semibold-14px text-gray-900">
          Actual Versions Overview
        </h3>
        <div className="flex h-6 w-full overflow-hidden rounded border border-gray-200">
          {versions.map((version) => {
            const totalCount = versionCounts[version];
            const failCount = versionFailureCounts[version] || 0;
            const okCount = totalCount - failCount;
            const color = colors[versionColorIndex[version] % colors.length];
            const widthPercent = (totalCount / totalWorkspaces) * 100;
            const failPercent =
              failCount > 0 ? (failCount / totalCount) * 100 : 0;
            const okPercent = 100 - failPercent;

            return (
              <div
                key={version}
                className="h-full"
                style={{
                  width: `${widthPercent}%`,
                  position: "relative",
                  height: "100%",
                }}
              >
                {/* Normal (ok) part */}
                <button
                  aria-pressed={selectedVersion === version}
                  className={`absolute top-0 left-0 z-[1] h-full w-full cursor-pointer rounded-none border-0 transition-all duration-300 focus:outline-none ${selectedVersion === version ? "outline outline-2 outline-[#222] outline-offset-[-2px]" : ""} `}
                  style={{
                    background: color,
                    clipPath:
                      failCount > 0
                        ? `inset(0 ${100 - okPercent}% 0 0)`
                        : undefined,
                  }}
                  title={`${version}: ${okCount} healthy workspaces (actual)`}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                />
                {/* Failure (dashed/orange) part */}
                {failCount > 0 && (
                  <button
                    aria-pressed={selectedVersion === version}
                    className={`absolute top-0 left-0 z-[2] h-full w-full cursor-pointer rounded-none border-0 opacity-100 transition-all duration-300 focus:outline-none ${selectedVersion === version ? "outline outline-2 outline-[#222] outline-offset-[-2px]" : ""} `}
                    style={{
                      background: `repeating-linear-gradient(135deg, ${color}, ${color} 6px, #fff 6px, #fff 12px)`,
                      clipPath: `inset(0 0 0 ${okPercent}%)`,
                    }}
                    title={`${version}: ${failCount} failed workspaces (actual)`}
                    type="button"
                    onClick={() => onSelectVersion(version)}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {selectedVersion
          ? allVersions
              .filter(
                (version) =>
                  version === selectedVersion && versionCounts[version] > 0,
              )
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={true}
                  className="flex items-center gap-1 rounded bg-indigo-50 px-2 py-1 font-inter-normal-12px ring-2 ring-indigo-500"
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-code-12">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0}) actual
                  </span>
                </button>
              ))
          : allVersions
              .filter((version) => versionCounts[version] > 0)
              .map((version) => (
                <button
                  key={version}
                  aria-pressed={selectedVersion === version}
                  className={`flex items-center gap-1 rounded px-2 py-1 font-inter-normal-12px ${
                    selectedVersion === version
                      ? "bg-indigo-50 ring-2 ring-indigo-500"
                      : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  type="button"
                  onClick={() => onSelectVersion(version)}
                >
                  <span
                    className="inline-block h-3 w-3 rounded"
                    style={{
                      background:
                        colors[versionColorIndex[version] % colors.length],
                    }}
                  />
                  <span className="font-code-12">{version}</span>
                  <span className="text-gray-500">
                    ({versionCounts[version] || 0}) actual
                  </span>
                </button>
              ))}
        {selectedVersion && (
          <button
            className="ml-2 font-inter-normal-12px text-gray-600 underline"
            type="button"
            onClick={() => onSelectVersion(null)}
          >
            Clear filter
          </button>
        )}
        {hasErrors && (
          <span className="font-inter-normal-12px text-amber-600">
            (Some workspaces failed to load)
          </span>
        )}
      </div>

      {/* Hidden div to trigger lazy loading */}
      <div ref={parentRef} className="hidden" />
    </div>
  );
};
